import cloneDeep from "lodash/cloneDeep";
import { v4 as uuidv4 } from "uuid";

import { type EditorNodeName, MathOperation } from "@/enums/customAttributes";
import { getServiceHelper } from "@/helpers/common";
import { SERVICE_CUSTOM } from "@/helpers/constants";
import {
  BLANK_NODES,
  CLOSING_PARENTHESIS,
} from "@/helpers/constants/attributeBuilder";
import parseDataServicesWorkerUrl from "@/workers/attributes/parseDataServicesWorker?worker&url";

import type { EditorNode } from "@/models/TextEditor";
import type {
  CustomAttributeFormula,
  GroupIdentifier,
} from "@/models/customAttributes/customAttribute";
import type {
  DataServiceInfo,
  IAttribute,
  ICustomAttributeContext,
  IDataService,
  IStepAttribute,
} from "@/models/orchestration";

enum FormulaValidationRules {
  notEmpty = "notEmpty",
  notStartWithOperator = "notStartWithOperator",
  notEndWithOperator = "notEndWithOperator",
  notTwoOperatorsInARow = "notTwoOperatorsInARow",
  notTwoOperandsInARow = "notTwoOperandsInARow",
  needContext = "needContext",
}

interface FormulaValidationResult {
  isValid: boolean;
  message: string;
}

const itemRequiresContext = (
  item: IStepAttribute,
  attributes: IAttribute[]
) => {
  const attribute = getServiceHelper(item.id, attributes, item.context);
  return (
    attribute?.options?.month_picker_range ||
    attribute?.options?.consumer_score_models_levels
  );
};

export const validateFormula = (
  formula: CustomAttributeFormula,
  attributes: IAttribute[] = []
): FormulaValidationResult => {
  if (!formula.length) {
    return {
      isValid: false,
      message: FormulaValidationRules.notEmpty,
    };
  }

  const isFirstItemOperator = typeof formula[0] === "string";
  if (isFirstItemOperator) {
    return {
      isValid: false,
      message: FormulaValidationRules.notStartWithOperator,
    };
  }

  const isLastItemOperator = typeof formula[formula.length - 1] === "string";
  if (isLastItemOperator) {
    return {
      isValid: false,
      message: FormulaValidationRules.notEndWithOperator,
    };
  }

  const mathOperations = Object.values(MathOperation);

  for (let i = 0; i < formula.length; i++) {
    const item = formula[i];

    if (Array.isArray(item)) {
      const result = validateFormula(item);
      if (!result.isValid) {
        return result;
      }
    } else if (
      // This checks if Attribute context is applied
      typeof item === "object" &&
      (Object.hasOwnProperty.call(item, "context") ||
        itemRequiresContext(item, attributes))
    ) {
      if (item.context === undefined) {
        return {
          isValid: false,
          message: FormulaValidationRules.needContext,
        };
      }
    }

    const nextItem = formula[i + 1];

    if (!nextItem) {
      break;
    }

    // This checks if operators are split by operands
    if (
      typeof item === "string" &&
      mathOperations.indexOf(item) > -1 &&
      typeof nextItem === "string" &&
      mathOperations.indexOf(nextItem) > -1
    ) {
      return {
        isValid: false,
        message: FormulaValidationRules.notTwoOperatorsInARow,
      };
    }
    // This checks if operands are split by operators
    if (typeof item !== "string" && typeof nextItem !== "string") {
      return {
        isValid: false,
        message: FormulaValidationRules.notTwoOperandsInARow,
      };
    }
  }

  return {
    isValid: true,
    message: "",
  };
};

export const getNestedFormulaReducer = (
  acc: CustomAttributeFormula,
  position: number
) => {
  const deeper = acc[position];
  if (Array.isArray(deeper)) {
    return deeper;
  }
  return acc;
};

export const getNestedFormula = (
  level: number[],
  formula: CustomAttributeFormula
) => {
  return level.reduce(getNestedFormulaReducer, formula);
};

export const isNumber = (token: string): boolean => {
  return /^-?\d+\.?\d*$/.test(token);
};

export const createEditorNode = (
  token: string,
  type: EditorNodeName
): EditorNode => {
  const newNode = cloneDeep(BLANK_NODES[type]);
  newNode.content = token;
  newNode.id = uuidv4();
  return newNode;
};

export const handleOperandToken = (
  token: EditorNode,
  attributeGroups: GroupIdentifier[],
  output: EditorNode[]
) => {
  token.argOf = attributeGroups.at(-1)?.id;
  output.push(token);
};

export const emptyOperatorAndParenthesesStack = (
  operatorsAndParenthesesStack: EditorNode[],
  attributeGroupsStack: GroupIdentifier[],
  outputStack: EditorNode[]
) => {
  while (
    operatorsAndParenthesesStack.length &&
    !CLOSING_PARENTHESIS.includes(
      operatorsAndParenthesesStack.at(-1)?.content ?? ""
    )
  ) {
    outputStack.push(operatorsAndParenthesesStack.pop() as EditorNode);
    attributeGroupsStack.pop();
  }
};

export const popOperatorAndPushToOutput = (
  operatorsAndParenthesesStack: EditorNode[],
  attributeGroups: GroupIdentifier[],
  output: EditorNode[]
) => {
  const poppedOperator = operatorsAndParenthesesStack.pop();
  attributeGroups.pop();
  if (poppedOperator) {
    poppedOperator.argOf = attributeGroups.at(-1)?.id;
    output.push(poppedOperator);
  }
};

export const handleClosingParenthesisToken = (
  token: EditorNode,
  operatorsAndParenthesesStack: EditorNode[],
  attributeGroups: GroupIdentifier[]
) => {
  if (token.id === undefined) {
    return;
  }
  operatorsAndParenthesesStack.push(token);
  attributeGroups.push({ id: token.id, isParanthesisGroup: true });
};

export const handleSeparatorToken = (
  operatorsAndParenthesesStack: EditorNode[],
  attributeGroups: GroupIdentifier[],
  output: EditorNode[],
  token: EditorNode
) => {
  emptyOperatorAndParenthesesStack(
    operatorsAndParenthesesStack,
    attributeGroups,
    output
  );
  const rightSideOperandFirst = output[output.length - 1];
  output.forEach((node) => {
    if (node.argOf === rightSideOperandFirst.argOf) {
      node.subGroup = token.id;
    }
  });
};

export const getServiceProviderMap = (services: IDataService[]) => {
  const parseDataServicesWorker = new Worker(parseDataServicesWorkerUrl, {
    type: "module",
  });

  return new Promise<Record<string, DataServiceInfo>>((resolve) => {
    parseDataServicesWorker.onmessage = (
      event: MessageEvent<Record<string, DataServiceInfo>>
    ) => {
      resolve(event.data);
      parseDataServicesWorker.terminate();
    };

    parseDataServicesWorker.postMessage(cloneDeep(services));
  });
};

export const getAttributeId = (attr: IStepAttribute | IAttribute) =>
  attr.id === SERVICE_CUSTOM
    ? (attr.context?.value as ICustomAttributeContext).id
    : attr.id;
